import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { FileText, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';

interface ReportInfo {
    id?: string | number;
    report_key: string;
    title: string;
    description: string;
    icon?: string;
    color?: string;
    is_custom?: boolean;
}

const ReportsDashboard: React.FC = () => {
    const navigate = useNavigate();
    const level = useClientLevel();
    const [reports, setReports] = useState<ReportInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Modal delete confirmation state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<{ id: any; name: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const openDeleteModal = (id: any, name: string) => {
        setReportToDelete({ id, name });
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setReportToDelete(null);
    };

    const handleDeleteReport = async () => {
        if (!reportToDelete) return;
        setIsDeleting(true);
        try {
            const response = await baseapi.delete(`report-templates/${reportToDelete.id}`);
            if (response.data.status) {
                showSuccess("Custom report deleted successfully");
                fetchReports();
                closeDeleteModal();
            } else {
                showError("Failed to delete custom report");
            }
        } catch (error) {
            console.error("Delete template error:", error);
            showError("Failed to delete custom report template");
        } finally {
            setIsDeleting(false);
        }
    };

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const params: any = {};
            if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get('/reports/available', { params });
            if (response.data.status) {
                setReports(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch reports', error);
            showError('Failed to load reports');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [level]);

    const getIcon = (iconName?: string, color?: string, reportKey: string = '') => {
        const IconComponent = (Icons as any)[iconName || ''] || Icons.FileText;

        const getColorClass = () => {
            if (color) return `text-${color}-500 dark:text-${color}-400`;

            const lowKey = reportKey.toLowerCase();
            if (lowKey.includes('asset')) return 'text-blue-600 dark:text-blue-400';
            if (lowKey.includes('event')) return 'text-orange-600 dark:text-orange-400';
            if (lowKey.includes('camera')) return 'text-emerald-600 dark:text-green-400';
            if (lowKey.includes('switch') || lowKey.includes('network') || lowKey.includes('interface')) return 'text-indigo-600 dark:text-indigo-400';
            if (lowKey.includes('poe')) return 'text-amber-600 dark:text-yellow-400';
            if (lowKey.includes('vlan')) return 'text-purple-600 dark:text-purple-400';
            if (lowKey.includes('lifecycle') || lowKey.includes('warranty')) return 'text-rose-600 dark:text-rose-400';
            return 'text-slate-600 dark:text-slate-400';
        };

        return <IconComponent className={`w-4 h-4 ${getColorClass()}`} />;
    };

    const getIconBg = (color?: string, reportKey: string = '') => {
        if (color) return `bg-${color}-500/10 border-${color}-500/20`;

        const lowKey = reportKey.toLowerCase();
        if (lowKey.includes('asset')) return 'bg-blue-500/10 border-blue-500/20';
        if (lowKey.includes('event')) return 'bg-orange-500/10 border-orange-500/20';
        if (lowKey.includes('camera')) return 'bg-green-500/10 border-green-500/20';
        if (lowKey.includes('switch') || lowKey.includes('network') || lowKey.includes('interface')) return 'bg-indigo-500/10 border-indigo-500/20';
        if (lowKey.includes('poe')) return 'bg-yellow-500/10 border-yellow-500/20';
        if (lowKey.includes('vlan')) return 'bg-purple-500/10 border-purple-500/20';
        if (lowKey.includes('lifecycle') || lowKey.includes('warranty')) return 'bg-rose-500/10 border-rose-500/20';
        return 'bg-slate-500/10 border-slate-500/20';
    };

    return (
        <div className="p-4 w-full min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-slate-200 mb-20 transition-colors duration-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 mt-1">
                <div className="flex items-center gap-3">
                    <div className="bg-orange-600 p-1.5 rounded shadow-lg shadow-orange-950/20">
                        <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Reports Dashboard</h1>
                </div>
                <button
                    onClick={() => navigate('/reports/custom-reports')}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-orange-950/20 transition-all flex items-center gap-2"
                >
                    <Icons.Plus className="w-3.5 h-3.5" />
                    Custom Reports
                </button>
            </div>

            {/* Main Content Container */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700/80 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl backdrop-blur-sm transition-colors duration-200">
                {/* Section Header */}
                <div className="px-3 py-2 border-b border-gray-200 dark:border-slate-700/60 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
                    <h2 className="text-[13px] font-bold text-gray-800 dark:text-slate-200 tracking-wide">All Reports</h2>
                    <div className="px-2.5 py-0.5 bg-gray-100 dark:bg-slate-800/80 rounded-full border border-gray-200 dark:border-slate-700">
                        <span className="text-[10px] font-bold text-gray-600 dark:text-slate-200 uppercase tracking-widest">{reports.length} Categories</span>
                    </div>
                </div>

                {/* List of Reports */}
                <div className="divide-y divide-gray-100 dark:divide-slate-800/40">
                    {isLoading ? (
                        <div className="py-20 flex flex-col items-center justify-center space-y-3">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                            <p className="text-xs font-medium text-gray-500 dark:text-slate-500 uppercase tracking-widest">Loading reports...</p>
                        </div>
                    ) : reports.length > 0 ? (
                        reports.map((report) => (
                            <div
                                key={report.report_key}
                                onClick={() => navigate(`/reports/${report.report_key}`)}
                                className="group flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800/20 border-b border-gray-100 dark:border-slate-700/60 transition-all cursor-pointer"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-xl border flex items-center justify-center shadow-inner ${getIconBg(report.color, report.report_key)}`}>
                                        {getIcon(report.icon, report.color, report.report_key)}
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="text-[14px] font-bold text-gray-900 dark:text-slate-100 group-hover:text-black dark:group-hover:text-white transition-colors tracking-tight">{report.title}</h3>
                                        <p className="text-[11px] text-gray-600 dark:text-slate-400 leading-relaxed max-w-2xl font-medium">{report.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2.5 p-1 px-2">
                                    {report.is_custom && (
                                        <>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/reports/custom-reports?mode=edit&id=${report.id}`);
                                                }}
                                                className="p-1.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 border border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-all shadow-sm"
                                                title="Edit Custom Report"
                                            >
                                                <Icons.Edit3 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDeleteModal(report.id, report.title);
                                                }}
                                                className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 hover:border-rose-500/30 rounded-lg text-rose-500 dark:text-rose-400 hover:text-rose-600 dark:hover:text-rose-300 transition-all shadow-sm"
                                                title="Delete Custom Report"
                                            >
                                                <Icons.Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </>
                                    )}
                                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-200 transition-all" />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-20 flex flex-col items-center justify-center space-y-3">
                            <AlertCircle className="w-10 h-10 text-gray-300 dark:text-slate-800" />
                            <p className="text-sm font-medium text-gray-500 dark:text-slate-500">No reports available at this time.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && reportToDelete && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeDeleteModal();
                    }}
                >
                    <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700/50 rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in duration-300 p-6 relative flex flex-col transition-colors">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                                <Icons.AlertTriangle className="text-rose-500 w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Delete Custom Report</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Are you sure you want to delete this report template?</p>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 mb-6 text-xs text-gray-700 dark:text-slate-300">
                            <p className="font-semibold text-gray-900 dark:text-slate-200 mb-1">{reportToDelete.name}</p>
                            <p className="text-[10px] text-rose-600 dark:text-rose-400/80 font-medium">This action cannot be undone and will permanently remove this custom configuration.</p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                disabled={isDeleting}
                                className="px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white transition-all disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteReport}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:bg-rose-800 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Icons.Trash2 className="w-3.5 h-3.5" />
                                )}
                                Delete Report
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsDashboard;
